import React, { useState } from "react";
import { Mail, MapPin, Phone, Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { BRAND } from "./data";
import { useRevealGroup } from "../../hooks/useReveal";

const FORMSPREE_ENDPOINT = "https://formspree.io/f/xnjylwwg";

const INTENTS = ["Buy", "Sell", "Invest", "Advisory", "Estates"];
const AREAS = [
  "Lutyens' Delhi",
  "South Delhi",
  "Gurugram",
  "Noida",
  "Greater Noida",
  "Faridabad",
  "Pan-India",
  "International",
];
const BUDGETS = ["₹1–5 Cr", "₹5–15 Cr", "₹15–50 Cr", "₹50 Cr+"];

const baseInputClass =
  "w-full bg-transparent border-b border-num-gold/30 focus:border-num-gold outline-none py-3.5 px-1 font-ui text-[13px] text-num-cream placeholder:text-num-cream/40 transition-colors";

const Contact = () => {
  const ref = useRevealGroup(".reveal");
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
    intent: "Buy",
    area_of_interest: "",
    budget: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.first_name || !form.last_name || !form.phone || !form.email) {
      toast.error("Please complete the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => data.append(k, v));
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: "POST",
        body: data,
        headers: { Accept: "application/json" },
      });
      if (!res.ok) throw new Error("Formspree error");
      setSubmitted(true);
      toast.success("Enquiry received. An advisor will reach out within one business day.");
      setForm({
        first_name: "",
        last_name: "",
        phone: "",
        email: "",
        intent: "Buy",
        area_of_interest: "",
        budget: "",
        message: "",
      });
    } catch (err) {
      console.error(err);
      toast.error("We could not submit your enquiry. Please try again or call directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      ref={ref}
      className="bg-forest-tex py-24 lg:py-32"
      data-testid="contact-section"
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20">
        {/* Left col */}
        <div className="lg:col-span-5">
          <div className="reveal flex items-center gap-3 mb-6">
            <span className="block w-10 h-px bg-num-gold" />
            <span className="font-ui text-[9px] uppercase text-num-gold track-eyebrow">
              Begin Your Journey
            </span>
          </div>
          <h2
            className="reveal delay-1 font-display font-medium text-num-cream leading-[1.05]"
            style={{ fontSize: "clamp(36px, 4.6vw, 56px)" }}
          >
            Speak to{" "}
            <span className="font-italic italic font-normal text-num-gold-light">an Advisor</span>
          </h2>
          <p className="reveal delay-2 mt-6 font-body italic text-[14px]" style={{ color: "rgba(247,243,236,0.55)" }}>
            Our advisors are available six days a week — entirely at your pace, entirely in
            confidence.
          </p>

          <div className="reveal delay-3 mt-10 space-y-5">
            <div className="flex items-start gap-4 text-num-cream/85">
              <MapPin size={18} className="text-num-gold mt-1 flex-shrink-0" />
              <div className="font-ui text-[13px] leading-relaxed">{BRAND.address}</div>
            </div>
            {BRAND.phones.map((p) => (
              <div key={p} className="flex items-center gap-4 text-num-cream/85">
                <Phone size={18} className="text-num-gold flex-shrink-0" />
                <a
                  href={`tel:${p.replace(/\s+/g, "")}`}
                  className="font-ui text-[13px] hover:text-num-gold transition-colors"
                  data-testid={`contact-phone-${p.replace(/\s+/g, "")}`}
                >
                  {p}
                </a>
              </div>
            ))}
            <div className="flex items-center gap-4 text-num-cream/85">
              <Mail size={18} className="text-num-gold flex-shrink-0" />
              <a
                href={`mailto:${BRAND.email}`}
                className="font-ui text-[13px] hover:text-num-gold transition-colors"
                data-testid="contact-email"
              >
                {BRAND.email}
              </a>
            </div>
            <div className="flex items-center gap-4 text-num-cream/85">
              <Clock size={18} className="text-num-gold flex-shrink-0" />
              <div className="font-ui text-[13px]">{BRAND.hours}</div>
            </div>
          </div>
        </div>

        {/* Form */}
        <form
          onSubmit={onSubmit}
          className="lg:col-span-7 reveal delay-2 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 bg-num-deep/40 backdrop-blur-sm border border-num-gold/15 p-8 lg:p-10"
          data-testid="enquiry-form"
        >
          <div>
            <label className="font-ui text-[9px] uppercase track-eyebrow text-num-gold/80">First Name *</label>
            <input
              required
              type="text"
              value={form.first_name}
              onChange={set("first_name")}
              className={baseInputClass}
              placeholder="Rajiv"
              data-testid="contact-first-name"
            />
          </div>
          <div>
            <label className="font-ui text-[9px] uppercase track-eyebrow text-num-gold/80">Last Name *</label>
            <input
              required
              type="text"
              value={form.last_name}
              onChange={set("last_name")}
              className={baseInputClass}
              placeholder="Malhotra"
              data-testid="contact-last-name"
            />
          </div>
          <div>
            <label className="font-ui text-[9px] uppercase track-eyebrow text-num-gold/80">Phone *</label>
            <input
              required
              type="tel"
              value={form.phone}
              onChange={set("phone")}
              className={baseInputClass}
              placeholder="+91 98100 00000"
              data-testid="contact-phone"
            />
          </div>
          <div>
            <label className="font-ui text-[9px] uppercase track-eyebrow text-num-gold/80">Email *</label>
            <input
              required
              type="email"
              value={form.email}
              onChange={set("email")}
              className={baseInputClass}
              placeholder="rajiv@malhotra.in"
              data-testid="contact-email-input"
            />
          </div>

          <div className="sm:col-span-2">
            <label className="font-ui text-[9px] uppercase track-eyebrow text-num-gold/80">I am looking to</label>
            <div className="flex flex-wrap gap-2 mt-3">
              {INTENTS.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, intent: opt }))}
                  className={`font-ui text-[10px] uppercase track-label px-4 py-2 border transition-colors ${
                    form.intent === opt
                      ? "border-num-gold bg-num-gold text-num-deep"
                      : "border-num-gold/30 text-num-cream/70 hover:border-num-gold hover:text-num-cream"
                  }`}
                  data-testid={`contact-intent-${opt.toLowerCase()}`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="font-ui text-[9px] uppercase track-eyebrow text-num-gold/80">Area of Interest</label>
            <select
              value={form.area_of_interest}
              onChange={set("area_of_interest")}
              className={`${baseInputClass} appearance-none bg-num-deep`}
              data-testid="contact-area"
            >
              <option value="">Select area</option>
              {AREAS.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="font-ui text-[9px] uppercase track-eyebrow text-num-gold/80">Budget · Ticket Size</label>
            <select
              value={form.budget}
              onChange={set("budget")}
              className={`${baseInputClass} appearance-none bg-num-deep`}
              data-testid="contact-budget"
            >
              <option value="">Select budget</option>
              {BUDGETS.map((b) => (
                <option key={b} value={b}>
                  {b}
                </option>
              ))}
            </select>
          </div>

          <div className="sm:col-span-2">
            <label className="font-ui text-[9px] uppercase track-eyebrow text-num-gold/80">Message — Optional</label>
            <textarea
              rows={3}
              value={form.message}
              onChange={set("message")}
              className={`${baseInputClass} resize-none border border-num-gold/20 focus:border-num-gold p-3`}
              placeholder="Tell us a little about what you are looking for…"
              data-testid="contact-message"
            />
          </div>

          <div className="sm:col-span-2 mt-3">
            <button
              type="submit"
              disabled={submitting}
              className="w-full btn-gold py-4 font-ui text-[11px] uppercase track-label disabled:opacity-60 inline-flex items-center justify-center gap-2"
              data-testid="contact-submit"
            >
              {submitting && <Loader2 size={14} className="animate-spin" />}
              {submitted && !submitting ? "Enquiry Received" : "Send Enquiry"}
            </button>
            <p className="mt-4 font-body italic text-[11px]" style={{ color: "rgba(247,243,236,0.45)" }}>
              By submitting this form, you consent to be contacted by a Numbardar advisor. Your
              details remain entirely confidential.
            </p>
          </div>
        </form>
      </div>
    </section>
  );
};

export default Contact;
